// ============================================================
// Gap Formula Generator
// ============================================================
import { GAP_TYPES } from '../data/gapKeywords';

/**
 * Generate the gap formula sentence.
 */
export function generateGapFormula({
  topGapType,
  keywords = [],
  topic = '',
  discipline = '',
  academicLevel = 'Masters',
}) {
  const gapLabel = GAP_TYPES[topGapType]?.label || 'Research Gap';
  const kwStr = keywords.slice(0, 3).join(', ');
  const context = discipline || 'the existing literature';

  return `Although prior studies have examined ${kwStr || topic}, little attention has been given to addressing the ${gapLabel.toLowerCase()} in [${context}]. Specifically, ${academicLevel === 'PhD' ? 'there is a need for intellectually innovative research that advances theoretical knowledge and methodological rigour within this domain' : 'there remains an opportunity for contextual or methodological investigation that can extend and apply existing knowledge to under-explored settings'}. The present study addresses this gap by focusing on [your specific contribution], thereby contributing to [${context}] and offering insights relevant to both scholars and practitioners.`;
}

/**
 * Generate the full 300+ word gap statement with inline citations.
 */
export function generateGapStatement({
  topic,
  discipline,
  academicLevel,
  keywords,
  topGap,
  secondGap,
  thirdGap,
  rankedGaps,
  processedArticles,
  validityScore,
}) {
  const gapLabel1 = GAP_TYPES[topGap?.type]?.label || 'primary gap';
  const gapLabel2 = GAP_TYPES[secondGap?.type]?.label || 'secondary gap';
  const gapLabel3 = GAP_TYPES[thirdGap?.type]?.label || 'tertiary gap';
  const kwStr = (keywords || []).slice(0, 5).join(', ');

  // Defensive guard — processedArticles must be an array
  const articles = Array.isArray(processedArticles) ? processedArticles : [];

  // Collect citations from top articles for each dominant gap
  const topCitations = articles
    .filter((a) => a.dominantGapType === topGap?.type)
    .slice(0, 3)
    .map((a) => `${a.authors} (${a.year})`);

  const secondCitations = articles
    .filter((a) => a.dominantGapType === secondGap?.type)
    .slice(0, 2)
    .map((a) => `${a.authors} (${a.year})`);

  // Space is embedded in cite strings so an empty cite leaves no orphan space/period
  const cite1 = topCitations.length > 0 ? ` (${topCitations.join('; ')})` : '';
  const cite2 = secondCitations.length > 0 ? ` (${secondCitations.join('; ')})` : '';
  const allCites = articles
    .slice(0, 5)
    .map((a) => `${a.authors} (${a.year})`)
    .join('; ') || 'see reviewed abstracts';

  const validityVerdict =
    validityScore?.percentage >= 70
      ? 'strongly validated'
      : validityScore?.percentage >= 50
      ? 'moderately supported'
      : 'tentatively identified';

  const levelNote =
    academicLevel === 'PhD'
      ? 'At the doctoral level, intellectual innovation and theoretical contribution are essential requirements for a credible research gap. The identified gap must extend beyond mere contextual application to offer genuine advancement of knowledge in the field.'
      : 'At the Master\'s level, demonstrating contextual application or methodological competence constitutes a credible research contribution. The identified gap reflects an opportunity to apply existing frameworks to under-explored settings or populations.';

  return `Research Gap Statement: ${topic || discipline}

The body of literature surrounding ${topic || kwStr} has grown substantially over recent decades, drawing on a wide range of theoretical traditions and empirical investigations. A systematic review of ${articles.length} scholarly abstracts focusing on the themes of ${kwStr} reveals that, despite this growth, significant lacunae persist in the academic discourse. A review of relevant literature — drawing on systematic gap identification methodology supported by Creswell and Creswell (2018) and Saunders, Lewis, and Thornhill (2019) — identifies three principal gaps in the literature, ranked by signal strength.

The most prominent gap detected is a **${gapLabel1}**${cite1}. The frequency and intensity of signals across the reviewed abstracts indicate that existing scholarship has yet to develop adequately the ${gapLabel1.toLowerCase()} dimension of ${topic || 'this topic'}. Multiple authors note ${GAP_TYPES[topGap?.type]?.description || 'this limitation'} as a priority area for future inquiry. This gap accounts for the highest aggregated score in the Limitations Matrix, with signals appearing in ${topGap?.articleCount || 'several'} of the reviewed articles (${Math.round(topGap?.percentage || 0)}% of the sample), confirming its prominence as the primary gap${cite1}.

The second-ranked gap is a **${gapLabel2}**${cite2}. Literature has recognised this gap through recurring references to limited or inconsistent treatment of the ${gapLabel2.toLowerCase()} dimension. ${GAP_TYPES[secondGap?.type]?.description || ''} This finding is corroborated by ${secondCitations.length > 0 ? secondCitations.join(' and ') : 'multiple studies'}, who call for future research to address this shortcoming. The presence of this gap alongside the primary gap suggests a compound research opportunity, where a single study may contribute on multiple fronts.

A tertiary **${gapLabel3}** was also detected, albeit at a lower signal strength. While less pronounced, this gap type reflects a recurring but underemphasised limitation in the reviewed abstracts. Addressing it — even partially — would add depth and nuance to any proposed research design, particularly for studies aiming to contribute beyond narrow disciplinary boundaries (Saunders et al., 2019; Creswell & Creswell, 2018).

${levelNote}

Validity Assessment: This gap has been ${validityVerdict} using the four-criterion validity framework (Unresolved, Contributory, Specific, Attainable), achieving a validity score of ${validityScore?.total || '—'}/20 (${validityScore?.percentage || 0}%). This score reflects the gap's merit as a legitimate and feasible research focus.

Based on this analysis, the proposed research addresses the identified ${gapLabel1.toLowerCase()} by [insert your specific contribution here]. This study draws on the following reviewed literature: ${allCites}.

References (from analysed abstracts):
${articles
  .map(
    (a, i) =>
      `${i + 1}. ${a.authors} (${a.year}). ${a.title}.`
  )
  .join('\n')}

Methodological Authorities:
- Saunders, M., Lewis, P., & Thornhill, A. (2019). Research Methods for Business Students (8th ed.). Pearson.
- Creswell, J. W., & Creswell, J. D. (2018). Research Design: Qualitative, Quantitative, and Mixed Methods Approaches (5th ed.). SAGE.
- Cochran, W. G. (1977). Sampling Techniques (3rd ed.). Wiley.
- Krejcie, R. V., & Morgan, D. W. (1970). Determining sample size for research activities. Educational and Psychological Measurement, 30(3), 607–610.`;
}
